/**
 * Image optimization utilities for person photos
 * Provides lazy loading, size optimization, and caching strategies
 */

import { getConfig } from '../config';

export interface ImageSizeConfig {
  width: number;
  height: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
}

export interface LazyLoadConfig {
  threshold: number;
  rootMargin: string;
  enableIntersectionObserver: boolean;
}

export interface ImageCacheConfig {
  maxAge: number; // in milliseconds
  maxSize: number; // maximum number of cached images
  enablePreloading: boolean;
}

/**
 * Predefined image size configurations for different contexts
 */
export const IMAGE_SIZES = {
  small: { width: 40, height: 40, quality: 80 },
  medium: { width: 60, height: 60, quality: 85 },
  large: { width: 80, height: 80, quality: 90 },
  thumbnail: { width: 32, height: 32, quality: 75 },
  card: { width: 48, height: 48, quality: 85 },
} as const;

/**
 * Default lazy loading configuration based on environment
 */
export const LAZY_LOAD_CONFIG: LazyLoadConfig = {
  threshold: 0.1,
  rootMargin: '50px',
  enableIntersectionObserver: true,
};

/**
 * Default image cache configuration
 */
export const IMAGE_CACHE_CONFIG: ImageCacheConfig = {
  maxAge: 30 * 60 * 1000, // 30 minutes
  maxSize: 200, // Cache up to 200 images
  enablePreloading: true,
};

/**
 * Optimizes image URL for specific size and quality requirements
 */
export function optimizeImageUrl(
  originalUrl: string,
  sizeConfig: ImageSizeConfig,
  cdnUrl?: string
): string {
  if (!originalUrl) return originalUrl;

  const config = getConfig();
  const baseCdnUrl = cdnUrl || config.imagesCdnUrl;

  // If no CDN is configured, return original URL
  if (!baseCdnUrl) {
    return originalUrl;
  }

  // Extract image path from original URL
  const imagePath = originalUrl.replace(/^https?:\/\/[^\/]+/, '');
  
  // Build optimized URL with size and quality parameters
  const params = new URLSearchParams({
    w: sizeConfig.width.toString(),
    h: sizeConfig.height.toString(),
    ...(sizeConfig.quality && { q: sizeConfig.quality.toString() }),
    ...(sizeConfig.format && { f: sizeConfig.format }),
    fit: 'cover', // Ensure proper aspect ratio
  });

  return `${baseCdnUrl}${imagePath}?${params}`;
}

/**
 * Gets the appropriate image size configuration based on context
 */
export function getImageSizeForContext(
  context: 'overlay' | 'grid' | 'detail' | 'thumbnail',
  size?: 'small' | 'medium' | 'large'
): ImageSizeConfig {
  // Context-based defaults
  const contextDefaults = {
    overlay: IMAGE_SIZES.small,
    grid: IMAGE_SIZES.medium,
    detail: IMAGE_SIZES.large,
    thumbnail: IMAGE_SIZES.thumbnail,
  };

  // Size overrides
  if (size && IMAGE_SIZES[size]) {
    return IMAGE_SIZES[size];
  }

  return contextDefaults[context] || IMAGE_SIZES.medium;
}

/**
 * Enhanced Intersection Observer for lazy loading with performance optimizations
 */
export class OptimizedIntersectionObserver {
  private observer: IntersectionObserver | null = null;
  private observedElements = new Map<Element, () => void>();
  private config: LazyLoadConfig;

  constructor(config: Partial<LazyLoadConfig> = {}) {
    this.config = { ...LAZY_LOAD_CONFIG, ...config };
    this.initializeObserver();
  }

  private initializeObserver(): void {
    if (!this.config.enableIntersectionObserver || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const callback = this.observedElements.get(entry.target);
            if (callback) {
              callback();
              this.unobserve(entry.target);
            }
          }
        });
      },
      {
        threshold: this.config.threshold,
        rootMargin: this.config.rootMargin,
      }
    );
  }

  public observe(element: Element, callback: () => void): void {
    if (!this.observer) {
      // Fallback: execute callback immediately if IntersectionObserver is not available
      callback();
      return;
    }

    this.observedElements.set(element, callback);
    this.observer.observe(element);
  }

  public unobserve(element: Element): void {
    if (this.observer) {
      this.observer.unobserve(element);
    }
    this.observedElements.delete(element);
  }

  public disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observedElements.clear();
  }
}

/**
 * Image cache for storing loaded images and their metadata
 */
export class ImageCache {
  private cache = new Map<string, {
    image: HTMLImageElement;
    loadedAt: number;
    size: number;
    hits: number;
  }>();
  private config: ImageCacheConfig;

  constructor(config: Partial<ImageCacheConfig> = {}) {
    this.config = { ...IMAGE_CACHE_CONFIG, ...config };
  }

  public get(url: string): HTMLImageElement | null {
    const cached = this.cache.get(url);
    if (!cached) return null;

    // Check if cache entry is still valid
    if (Date.now() - cached.loadedAt > this.config.maxAge) {
      this.cache.delete(url);
      return null;
    }

    // Update hit count for LRU eviction
    cached.hits++;
    return cached.image;
  }

  public set(url: string, image: HTMLImageElement): void {
    // Evict old entries if cache is full
    if (this.cache.size >= this.config.maxSize) {
      this.evictLeastRecentlyUsed();
    }

    this.cache.set(url, {
      image,
      loadedAt: Date.now(),
      size: this.estimateImageSize(image),
      hits: 1,
    });
  }

  public preload(urls: string[]): Promise<void[]> {
    if (!this.config.enablePreloading) {
      return Promise.resolve([]);
    }

    const preloadPromises = urls.map((url) => {
      // Skip if already cached
      if (this.cache.has(url)) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          this.set(url, img);
          resolve();
        };
        img.onerror = () => {
          reject(new Error(`Failed to preload image: ${url}`));
        };
        img.src = url;
      });
    });

    return Promise.allSettled(preloadPromises).then(() => []);
  }

  public clear(): void {
    this.cache.clear();
  }

  public getStats(): {
    size: number;
    totalHits: number;
    estimatedMemoryUsage: number;
  } {
    let totalHits = 0;
    let estimatedMemoryUsage = 0;

    for (const entry of this.cache.values()) {
      totalHits += entry.hits;
      estimatedMemoryUsage += entry.size;
    }

    return {
      size: this.cache.size,
      totalHits,
      estimatedMemoryUsage,
    };
  }

  private evictLeastRecentlyUsed(): void {
    let lruKey: string | null = null;
    let lruHits = Infinity;
    let lruTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.hits < lruHits || (entry.hits === lruHits && entry.loadedAt < lruTime)) {
        lruKey = key;
        lruHits = entry.hits;
        lruTime = entry.loadedAt;
      }
    }

    if (lruKey) {
      this.cache.delete(lruKey);
    }
  }

  private estimateImageSize(image: HTMLImageElement): number {
    // Rough estimation: width * height * 4 bytes per pixel (RGBA)
    return (image.naturalWidth || 100) * (image.naturalHeight || 100) * 4;
  }
}

/**
 * Performance monitoring for image loading
 */
export class ImagePerformanceMonitor {
  private metrics = new Map<string, {
    loadStartTime: number;
    loadEndTime?: number;
    loadDuration?: number;
    cacheHit: boolean;
    size: ImageSizeConfig;
    error?: string;
  }>();

  public startLoad(url: string, size: ImageSizeConfig, cacheHit = false): void {
    this.metrics.set(url, {
      loadStartTime: performance.now(),
      cacheHit,
      size,
    });
  }

  public endLoad(url: string, success = true, error?: string): void {
    const metric = this.metrics.get(url);
    if (!metric) return;

    const endTime = performance.now();
    metric.loadEndTime = endTime;
    metric.loadDuration = endTime - metric.loadStartTime;
    
    if (!success && error) {
      metric.error = error;
    }
  }

  public getMetrics(): Array<{
    url: string;
    loadDuration?: number;
    cacheHit: boolean;
    size: ImageSizeConfig;
    error?: string;
  }> {
    return Array.from(this.metrics.entries()).map(([url, metric]) => ({
      url,
      loadDuration: metric.loadDuration,
      cacheHit: metric.cacheHit,
      size: metric.size,
      error: metric.error,
    }));
  }

  public getAverageLoadTime(): number {
    const completedLoads = Array.from(this.metrics.values())
      .filter(m => m.loadDuration !== undefined && !m.cacheHit);
    
    if (completedLoads.length === 0) return 0;
    
    const totalTime = completedLoads.reduce((sum, m) => sum + (m.loadDuration || 0), 0);
    return totalTime / completedLoads.length;
  }

  public getCacheHitRate(): number {
    const total = this.metrics.size;
    if (total === 0) return 0;
    
    const cacheHits = Array.from(this.metrics.values())
      .filter(m => m.cacheHit).length;
    
    return (cacheHits / total) * 100;
  }

  public clear(): void {
    this.metrics.clear();
  }
}

// Global instances for shared use
export const globalImageCache = new ImageCache();
export const globalPerformanceMonitor = new ImagePerformanceMonitor();

/**
 * Utility function to create optimized image loading with all performance features
 */
export function createOptimizedImageLoader(config?: {
  lazyLoad?: Partial<LazyLoadConfig>;
  cache?: Partial<ImageCacheConfig>;
  enableMonitoring?: boolean;
}) {
  const observer = new OptimizedIntersectionObserver(config?.lazyLoad);
  const cache = new ImageCache(config?.cache);
  const monitor = config?.enableMonitoring ? new ImagePerformanceMonitor() : null;

  return {
    observer,
    cache,
    monitor,
    
    loadImage: (
      url: string,
      element: Element,
      sizeConfig: ImageSizeConfig,
      onLoad?: (img: HTMLImageElement) => void,
      onError?: (error: Error) => void
    ) => {
      const optimizedUrl = optimizeImageUrl(url, sizeConfig);
      
      // Check cache first
      const cachedImage = cache.get(optimizedUrl);
      if (cachedImage) {
        monitor?.startLoad(optimizedUrl, sizeConfig, true);
        monitor?.endLoad(optimizedUrl, true);
        onLoad?.(cachedImage);
        return;
      }

      // Use intersection observer for lazy loading
      observer.observe(element, () => {
        monitor?.startLoad(optimizedUrl, sizeConfig, false);
        
        const img = new Image();
        img.onload = () => {
          cache.set(optimizedUrl, img);
          monitor?.endLoad(optimizedUrl, true);
          onLoad?.(img);
        };
        img.onerror = () => {
          const error = new Error(`Failed to load image: ${optimizedUrl}`);
          monitor?.endLoad(optimizedUrl, false, error.message);
          onError?.(error);
        };
        img.src = optimizedUrl;
      });
    },

    preloadImages: (urls: string[], sizeConfig: ImageSizeConfig) => {
      const optimizedUrls = urls.map(url => optimizeImageUrl(url, sizeConfig));
      return cache.preload(optimizedUrls);
    },

    cleanup: () => {
      observer.disconnect();
      cache.clear();
      monitor?.clear();
    },

    getStats: () => ({
      cache: cache.getStats(),
      performance: monitor ? {
        averageLoadTime: monitor.getAverageLoadTime(),
        cacheHitRate: monitor.getCacheHitRate(),
        totalMetrics: monitor.getMetrics().length,
      } : null,
    }),
  };
}